/**
 * Groesse der Video-Flaeche in der Szene bestimmen.
 *
 * ZWEI QUELLEN, die man auseinanderhalten muss (Jans Fund 04.08.2026: "das
 * Video wird bei natuerlich geringer px-Groesse viel kleiner platziert"):
 *
 *  - Das SEITENVERHAELTNIS muss aus der geladenen Datei kommen. Die im Layer
 *    gespeicherten Masse stimmen nicht zwangslaeufig: bei .mov mit
 *    Rotations-Metadaten (Hochformat vom Telefon) stehen dort die unrotierten
 *    Container-Masse, das Video wurde dadurch verzerrt (frueherer Fund).
 *
 *  - Die GROESSE muss aus dem Layer kommen — dem Platz, den der Gestalter im
 *    Template vorgesehen hat. Frueher stammte auch sie aus der Datei: ein
 *    360x640-Video ergab eine 360x640-Flaeche, egal wie gross der Platz war.
 *    Nachgemessen lagen beide Videos in Jans Scrolly bei 360x640, waehrend das
 *    Bild daneben 3072x4096 mass — deshalb wirkte ein getauschtes Video winzig.
 *
 * REGEL: Hoehe des Platzes behalten, Breite aus dem echten Seitenverhaeltnis
 * ableiten. Das ist dieselbe Rechnung, die der Template-Editor beim Tausch
 * schon anwendet (updateMediaLayerUrl: `pixelWidth = calcHeight * aspect`) —
 * die Flaeche in der Szene folgt ihr jetzt auch.
 *
 * Ohne Platz-Angabe bleibt es beim bisherigen Verhalten (Datei-Masse), damit
 * Bestands-Scrollys ohne calc- und Content-Masse sich nicht veraendern.
 */
export function videoPlaneSize(
    dateiBreite: number | undefined,
    dateiHoehe: number | undefined,
    calcWidth?: number,
    calcHeight?: number,
    contentWidth?: number,
    contentHeight?: number,
): { w: number; h: number } {
    const gueltig = (n?: number) => typeof n === "number" && Number.isFinite(n) && n > 0;

    // Platz-Hoehe: calc* ist der bewusst gerechnete Wert und gewinnt.
    const platzHoehe = gueltig(calcHeight)
        ? (calcHeight as number)
        : gueltig(contentHeight)
          ? (contentHeight as number)
          : 0;

    const seitenverhaeltnis =
        gueltig(dateiBreite) && gueltig(dateiHoehe)
            ? (dateiBreite as number) / (dateiHoehe as number)
            : 0;

    if (platzHoehe > 0 && seitenverhaeltnis > 0) {
        return { w: Math.round(platzHoehe * seitenverhaeltnis), h: platzHoehe };
    }

    // Rueckfall: bisheriges Verhalten.
    const w = gueltig(dateiBreite)
        ? (dateiBreite as number)
        : gueltig(calcWidth)
          ? (calcWidth as number)
          : gueltig(contentWidth)
            ? (contentWidth as number)
            : 1;
    const h = gueltig(dateiHoehe)
        ? (dateiHoehe as number)
        : platzHoehe > 0
          ? platzHoehe
          : 1;
    return { w, h };
}

/**
 * Dieselbe Regel fuer BILDER (Jans Auftrag 24.09.2026: „es darf nie etwas
 * verzerrt werden").
 *
 * Bis hierher baute der Reader die Bildflaeche stur aus den Massen der Ebene
 * (`calcWidth/calcHeight`) und zog die Textur darueber. Stimmten Ebene und
 * Bild nicht ueberein, wurde gestreckt — und der Renderer hatte dagegen keine
 * Sicherung. Fuer Videos gibt es sie seit dem 04.08.2026, fuer Bilder nicht.
 *
 * Jetzt gilt fuer beide: HOEHE vom Platz, BREITE aus dem echten
 * Seitenverhaeltnis der geladenen Datei. Damit ist Verzerren strukturell
 * unmoeglich — unabhaengig davon, was in den Daten steht und ueber welchen
 * Weg das Bild hereinkam. Stimmen Daten und Datei ueberein (der Normalfall
 * seit dem Zuschnitt-Fix), aendert sich nichts.
 *
 * Ohne Platz-Angabe bleibt es beim bisherigen Verhalten.
 */
export function bildPlaneSize(
    dateiBreite: number | undefined,
    dateiHoehe: number | undefined,
    calcWidth?: number,
    calcHeight?: number,
    contentWidth?: number,
    contentHeight?: number,
): { w: number; h: number } {
    return videoPlaneSize(dateiBreite, dateiHoehe, calcWidth, calcHeight, contentWidth, contentHeight);
}
